import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://ccc4cs.choicegroup.in/api';

// Calculate total pounds from pack_size, cartons, and loose_bags
const calculateTotalPounds = (packSize, cartons, looseBags = 0) => {
  if (!packSize || (!cartons && !looseBags)) return 0;
  
  // Replace X with / for consistency
  const normalizedPackSize = packSize.replace(/X/gi, '/');
  
  // Parse pack_size format: "X/Y UNIT" (e.g., "3/7 LB", "12/16 OZ", "1/10 KG")
  const match = normalizedPackSize.match(/^(\d+)\/(\d+)\s*(LB|OZ|KG)$/i);
  
  if (!match) {
    // Handle special cases like "LOOSE KG"
    if (normalizedPackSize.toUpperCase() === 'LOOSE KG') {
      return 0; // Can't calculate without weight info
    }
    return 0;
  }
  
  const bagsPerCarton = parseInt(match[1], 10);
  const weightPerBag = parseInt(match[2], 10);
  const unit = match[3].toUpperCase();
  
  let totalPounds = 0;
  
  if (unit === 'LB') {
    // Formula: bags_per_carton * weight_per_bag * cartons + loose_bags * weight_per_bag
    totalPounds = (bagsPerCarton * weightPerBag * cartons) + (looseBags * weightPerBag);
  } else if (unit === 'OZ') {
    // Formula: (bags_per_carton * weight_per_bag * cartons) / 16 + (loose_bags * weight_per_bag) / 16
    totalPounds = ((bagsPerCarton * weightPerBag * cartons) / 16) + ((looseBags * weightPerBag) / 16);
  } else if (unit === 'KG') {
    // Formula: bags_per_carton * weight_per_bag * cartons * 2.2 + loose_bags * weight_per_bag * 2.2
    totalPounds = (bagsPerCarton * weightPerBag * cartons * 2.2) + (looseBags * weightPerBag * 2.2);
  }
  
  return Math.round(totalPounds * 100) / 100; // Round to 2 decimal places
};

const MarkExcessModal = ({ open, onClose, shipment, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [excessDetails, setExcessDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = ['Select Items', 'Enter Excess Quantities', 'New Pallet Details', 'Confirm'];

  const handleClose = () => {
    setActiveStep(0);
    setSelectedItems([]);
    setExcessDetails({});
    setError('');
    onClose();
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleExcessQuantityChange = (itemId, field, value) => {
    setExcessDetails(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedItems.length === 0) {
      setError('Please select at least one item');
      return;
    }

    if (activeStep === 1) {
      // Validate excess quantities
      for (const itemId of selectedItems) {
        const details = excessDetails[itemId];
        const item = shipment.items.find(i => i.id === itemId);
        
        const excessCartons = parseInt(details?.excess_cartons) || 0;
        const excessLooseBags = parseInt(details?.excess_loose_bags) || 0;
        
        // At least one of cartons or loose bags must be provided
        if (excessCartons === 0 && excessLooseBags === 0) {
          setError(`Please enter excess cartons or loose bags for Pallet ${item.pallet_id || itemId}`);
          return;
        }
        
        if (excessCartons > item.cartons) {
          setError(`Excess cartons cannot exceed available cartons (${item.cartons}) for Pallet ${item.pallet_id || itemId}`);
          return;
        }
        
        if (excessLooseBags > (item.loose_bags || 0)) {
          setError(`Excess loose bags cannot exceed available (${item.loose_bags || 0}) for Pallet ${item.pallet_id || itemId}`);
          return;
        }
      }
    }

    if (activeStep === 2) {
      // Validate new pallet details
      for (const itemId of selectedItems) {
        const details = excessDetails[itemId];
        const item = shipment.items.find(i => i.id === itemId);
        
        if (!details.new_pallet_id || details.new_pallet_id.trim() === '') {
          setError(`Please enter new Pallet ID for original Pallet ${item.pallet_id || itemId}`);
          return;
        }
      }
    }

    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const excess_items = selectedItems.map(itemId => {
        const details = excessDetails[itemId];
        return {
          original_id: itemId,
          excess_cartons: parseInt(details.excess_cartons) || 0,
          excess_loose_bags: parseInt(details.excess_loose_bags) || 0,
          new_pallet_id: details.new_pallet_id,
          new_cs1: details.new_cs1 || '',
          new_cs2: details.new_cs2 || '',
          reason: details.reason || 'Excess cartons/bags returned'
        };
      });

      // Get token from localStorage and include in headers
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE_URL}/mark_excess.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ excess_items })
      });

      const result = await response.json();

      if (result.success) {
        alert(`✅ Successfully processed ${result.data.length} excess entries`);
        onSuccess();
        handleClose();
      } else {
        setError(result.error || 'Failed to mark excess');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Select items that have excess cartons/bags to return:
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: '#1565c0', color: 'white' }}>Select</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white' }}>Pallet ID</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white' }}>Product</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white' }}>Grade</TableCell>
                    <TableCell sx={{ bgcolor: '#1565c0', color: 'white' }}>Pack Size</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white' }}>Cartons</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white' }}>Loose Bags</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white' }}>Total Pounds</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipment.items.map((item) => {
                    const looseBags = parseInt(item.loose_bags) || 0;
                    const totalPounds = calculateTotalPounds(item.pack_size, item.cartons, looseBags);
                    return (
                      <TableRow key={item.id} hover selected={selectedItems.includes(item.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleItemSelect(item.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{item.pallet_id || '-'}</TableCell>
                        <TableCell>{item.product}</TableCell>
                        <TableCell>{item.grade}</TableCell>
                        <TableCell>{item.pack_size || '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{item.cartons}</TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontWeight: 600, 
                            color: looseBags > 0 ? '#d32f2f' : 'text.secondary'
                          }}
                        >
                          {looseBags}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1565c0' }}>
                          {totalPounds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Selected: {selectedItems.length} item(s)
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Enter excess quantities for each selected item:
            </Typography>
            {selectedItems.map((itemId, index) => {
              const item = shipment.items.find(i => i.id === itemId);
              const details = excessDetails[itemId] || {};
              const excessCartons = parseInt(details.excess_cartons) || 0;
              const excessLooseBags = parseInt(details.excess_loose_bags) || 0;
              const remainingCartons = item.cartons - excessCartons;
              const remainingLooseBags = (item.loose_bags || 0) - excessLooseBags;
              const looseBags = parseInt(item.loose_bags) || 0;
              const totalPounds = calculateTotalPounds(item.pack_size, item.cartons, looseBags);

              return (
                <Paper key={itemId} sx={{ p: 2, mb: 2, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Item {index + 1} of {selectedItems.length}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                    <Typography variant="body2">
                      <strong>Pallet:</strong> {item.pallet_id || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Product:</strong> {item.product}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Grade:</strong> {item.grade}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Pack Size:</strong> {item.pack_size || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1565c0' }}>
                      <strong>Total Pounds:</strong> {totalPounds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Excess Cartons"
                        type="number"
                        value={details.excess_cartons || ''}
                        onChange={(e) => handleExcessQuantityChange(itemId, 'excess_cartons', e.target.value)}
                        inputProps={{ min: 0, max: item.cartons }}
                        helperText={`Available: ${item.cartons} | Remaining: ${remainingCartons}`}
                        error={excessCartons > item.cartons}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Excess Loose Bags"
                        type="number"
                        value={details.excess_loose_bags || ''}
                        onChange={(e) => handleExcessQuantityChange(itemId, 'excess_loose_bags', e.target.value)}
                        inputProps={{ min: 0, max: looseBags }}
                        helperText={
                          <span style={{ color: looseBags > 0 ? '#d32f2f' : 'inherit' }}>
                            Available: {looseBags} | Remaining: {remainingLooseBags}
                          </span>
                        }
                        error={excessLooseBags > looseBags}
                        disabled={looseBags === 0}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: looseBags > 0 ? '#d32f2f' : 'inherit'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    fullWidth
                    label="Reason"
                    value={details.reason || ''}
                    onChange={(e) => handleExcessQuantityChange(itemId, 'reason', e.target.value)}
                    placeholder="Why are these being returned?"
                    multiline
                    rows={2}
                    sx={{ mt: 2 }}
                  />
                </Paper>
              );
            })}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Enter new pallet details for excess items:
            </Typography>
            {selectedItems.map((itemId, index) => {
              const item = shipment.items.find(i => i.id === itemId);
              const details = excessDetails[itemId] || {};
              const excessLooseBags = parseInt(details.excess_loose_bags) || 0;

              return (
                <Paper key={itemId} sx={{ p: 2, mb: 2, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Item {index + 1} of {selectedItems.length}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                    <Typography variant="body2">
                      <strong>Original Pallet:</strong> {item.pallet_id || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Excess Cartons:</strong> {details.excess_cartons || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: excessLooseBags > 0 ? '#d32f2f' : 'inherit' }}>
                      <strong>Excess Loose Bags:</strong> {excessLooseBags}
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="New Pallet ID *"
                    value={details.new_pallet_id || ''}
                    onChange={(e) => handleExcessQuantityChange(itemId, 'new_pallet_id', e.target.value)}
                    placeholder="Enter new pallet ID for excess items"
                    sx={{ mb: 2 }}
                  />

                  <Box display="flex" gap={2}>
                    <TextField
                      fullWidth
                      label="CS1"
                      value={details.new_cs1 || ''}
                      onChange={(e) => handleExcessQuantityChange(itemId, 'new_cs1', e.target.value)}
                      placeholder="Cold Storage 1"
                    />
                    <TextField
                      fullWidth
                      label="CS2"
                      value={details.new_cs2 || ''}
                      onChange={(e) => handleExcessQuantityChange(itemId, 'new_cs2', e.target.value)}
                      placeholder="Cold Storage 2"
                    />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Review & Confirm
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please review the details before confirming
            </Alert>

            {selectedItems.map((itemId, index) => {
              const item = shipment.items.find(i => i.id === itemId);
              const details = excessDetails[itemId] || {};
              const excessCartons = parseInt(details.excess_cartons) || 0;
              const excessLooseBags = parseInt(details.excess_loose_bags) || 0;
              const remainingCartons = item.cartons - excessCartons;
              const remainingLooseBags = (item.loose_bags || 0) - excessLooseBags;
              const looseBags = parseInt(item.loose_bags) || 0;
              const totalPounds = calculateTotalPounds(item.pack_size, item.cartons, looseBags);

              return (
                <Paper key={itemId} sx={{ p: 2, mb: 2 }} elevation={2}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Item {index + 1}: {item.product} {item.grade} | {item.pack_size}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Original Pallet:</strong> {item.pallet_id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>New Pallet:</strong> {details.new_pallet_id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Total Cartons:</strong> {item.cartons}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Excess Cartons:</strong> {excessCartons}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: (item.loose_bags || 0) > 0 ? '#d32f2f' : 'inherit' }}>
                        <strong>Total Loose Bags:</strong> {item.loose_bags || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: excessLooseBags > 0 ? '#d32f2f' : 'inherit' }}>
                        <strong>Excess Loose Bags:</strong> {excessLooseBags}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="success.main">
                        <strong>Remaining Cartons:</strong> {remainingCartons}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: remainingLooseBags > 0 ? '#2e7d32' : 'text.secondary' }}>
                        <strong>Remaining Loose Bags:</strong> {remainingLooseBags}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ color: '#1565c0' }}>
                        <strong>Total Pounds:</strong> {totalPounds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Grid>
                    {details.new_cs1 && (
                      <Grid item xs={6}>
                        <Typography variant="body2"><strong>CS1:</strong> {details.new_cs1}</Typography>
                      </Grid>
                    )}
                    {details.new_cs2 && (
                      <Grid item xs={6}>
                        <Typography variant="body2"><strong>CS2:</strong> {details.new_cs2}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              );
            })}
          </Box>
        );

      default:
        return null;
    }
  };

  if (!shipment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Mark Excess Cartons/Bags - {shipment.fcl_no}
        <Typography variant="body2" color="text.secondary">
          {shipment.fcl_month} • {shipment.items.length} items
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<BackIcon />} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ForwardIcon />}
            disabled={loading}
          >
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm & Process'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MarkExcessModal;